import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CopyableHash,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shared/ui';
import type { InvoiceTransaction } from '@entities/invoice';
import {
  formatAmlRiskscorePercent,
  getPositiveAmlSignals,
} from '@entities/invoice';
import { formatCryptoAmount } from '@shared/lib/format-amount';
import { formatDateTime, formatTableDateTime } from '@shared/lib/format-date';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';

interface TransactionAmlModalProps {
  transaction: InvoiceTransaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TransactionAmlModal({
  transaction,
  open,
  onOpenChange,
}: TransactionAmlModalProps) {
  const { t } = useTranslation('cabinet');
  const { copy } = useCopyToClipboard();
  const aml = transaction?.aml ?? null;

  const positiveSignals = useMemo(
    () => getPositiveAmlSignals(aml?.signals),
    [aml?.signals]
  );

  const formattedCheckAt = aml?.checkAt ? formatDateTime(aml.checkAt) : '-';

  const handleDownloadPdf = () => {
    if (aml?.pdf) {
      window.open(aml.pdf, '_blank');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('payments.aml.modalTitle')}</DialogTitle>
        </DialogHeader>

        {aml && transaction ? (
          <div className="flex flex-col gap-4 text-sm">
            <div className="grid grid-cols-1 gap-y-1 sm:grid-cols-[140px_1fr] sm:gap-y-2">
              <span className="text-muted-foreground">{t('payments.aml.riskScore')}</span>
              <span className="font-semibold">{formatAmlRiskscorePercent(aml.riskscore)}</span>

              <span className="text-muted-foreground">{t('payments.aml.riskyVolume')}</span>
              <span>{aml.riskyVolume ?? '-'}</span>

              <span className="text-muted-foreground">{t('payments.aml.checkAt')}</span>
              <span>{formattedCheckAt}</span>
            </div>

            {positiveSignals.length > 0 && (
              <div>
                <h4 className="mb-2 font-semibold">{t('payments.aml.signals')}</h4>
                <ul className="flex flex-col gap-1">
                  {positiveSignals.map((signal) => (
                    <li
                      key={signal.key}
                      className="flex justify-between gap-3 border-b border-border py-1"
                    >
                      <span className="capitalize">{signal.label}</span>
                      <span className="font-medium">{signal.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <h4 className="mb-2 font-semibold">{t('payments.aml.transactionDetails')}</h4>
              <div className="grid grid-cols-1 gap-y-1 sm:grid-cols-[140px_1fr] sm:gap-y-2">
                <span className="text-muted-foreground">{t('payments.modal.txid')}</span>
                <CopyableHash
                  value={transaction.txid}
                  onCopy={() => copy(transaction.txid)}
                />

                <span className="text-muted-foreground">{t('payments.modal.amount')}</span>
                <span>
                  {formatCryptoAmount(transaction.amount, transaction.asset, true)} {transaction.asset}
                  {transaction.network ? ` (${transaction.network})` : ''}
                </span>

                <span className="text-muted-foreground">{t('payments.modal.date')}</span>
                <span>{formatTableDateTime(transaction.time)}</span>

                <span className="text-muted-foreground">{t('payments.aml.addressFrom')}</span>
                <button
                  type="button"
                  className="break-all text-left text-primary hover:underline"
                  onClick={() => transaction.addressFrom && void copy(transaction.addressFrom)}
                >
                  {transaction.addressFrom || '-'}
                </button>

                <span className="text-muted-foreground">{t('payments.aml.addressTo')}</span>
                <button
                  type="button"
                  className="break-all text-left text-primary hover:underline"
                  onClick={() => transaction.addressTo && void copy(transaction.addressTo)}
                >
                  {transaction.addressTo || '-'}
                </button>
              </div>
            </div>

            {aml.pdf && (
              <Button type="button" className="w-full" onClick={handleDownloadPdf}>
                {t('payments.aml.downloadPdf')}
              </Button>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
